import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Descriptions, Card, Spin, Button, Space, Tag, Empty, Row, Col } from 'antd';
import { ArrowLeftOutlined, MailOutlined, PhoneOutlined, GlobalOutlined, CalendarOutlined, UserOutlined, HomeOutlined, TeamOutlined, DollarOutlined } from '@ant-design/icons';
import { WebApiMemberDtoMemberDto, WebApiModelsMemberField, WebApiModelsMembershipProduct } from '../../Repository/eventbookyapi';
import { MemberService } from '../../Services/MemberService';
import { MemberFieldService } from '../../Services/MemberFieldService';
import { MembershipProductService } from '../../Services/MembershipProductService';
import { TextService } from '../../Services/TextService';
import { AuthProvider } from '../../Providers/authProvider';
import { AuthenticatedUser } from '../../Types/AuthenticatedUser';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import LeftMenu from './LeftMenu';
import BookyBox from '../../Components/Layout/BookyBox';
import { formatDateString } from '../../Helpers/DateHelpers';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import EditMember from '../../Components/Users/EditMember';
import AddNote from '../../Components/Users/AddNote';
import AddPayment from '../../Components/Users/AddPayment';
import { MemberStatusType } from '../../Types/MemberStatusType';
import { CompanyTypeConfiguration } from '../../Types/CompanyTypes';

const MemberDetails = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const GetText = TextService.GetText;

    const [member, setMember] = useState<WebApiMemberDtoMemberDto | null>(null);
    const [memberFieldSettings, setMemberFieldSettings] = useState<WebApiModelsMemberField[]>([]);
    const [membershipProducts, setMembershipProducts] = useState<WebApiModelsMembershipProduct[]>([]);
    const [user, setUser] = useState<AuthenticatedUser | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadMemberDetails();
    }, [id]);

    const loadMemberDetails = async () => {
        try {
            setLoading(true);
            const [userData, memberData, fields, products] = await Promise.all([
                AuthProvider().GetLoggedInUser(),
                MemberService.GetAllMembers(), // We'll filter by id
                MemberFieldService.GetMemberFields(),
                MembershipProductService.GetAllMembershipProducts()
            ]);

            setUser(userData);
            setMemberFieldSettings(fields);
            setMembershipProducts(products);

            // Find the specific member by memberId
            const foundMember = memberData.members?.find(m => m.memberId === Number(id));
            if (foundMember) {
                setMember(foundMember);
            } else {
                // Member not found
                setMember(null);
            }
        } catch (error) {
            console.error('Error loading member details:', error);
        } finally {
            setLoading(false);
        }
    };

    const getMembershipProductName = (productId?: number) => {
        if (!productId) return '-';
        const product = membershipProducts.find(p => p.id === productId);
        return product?.name || '-';
    };

    const getMembershipProductNames = (productIds?: number[] | null) => {
        if (!productIds || productIds.length === 0) return '-';
        const names = productIds
            .map(id => membershipProducts.find(p => p.id === id)?.name)
            .filter(Boolean);
        return names.length > 0 ? names.join(', ') : '-';
    };

    const getStatusTag = (status?: MemberStatusType) => {
        switch (status) {
            case MemberStatusType.Active:
                return <Tag color="green">{GetText("MemberList_StatusActive")}</Tag>;
            case MemberStatusType.Cancelled:
                return <Tag color="red">{GetText("MemberList_StatusCancelled")}</Tag>;
            case MemberStatusType.UnconfirmedEmail:
                return <Tag color="orange">{GetText("MemberList_StatusUnconfirmedEmail")}</Tag>;
            default:
                return <Tag>{GetText("MemberList_StatusActive")}</Tag>;
        }
    };

    const getPaymentStatusTag = (isPaid?: boolean) => {
        return isPaid
            ? <Tag color="green">{GetText("MemberList_Paid")}</Tag>
            : <Tag color="red">{GetText("MemberList_Unpaid")}</Tag>;
    };

    if (loading) {
        return (
            <LayoutLeftMenu selected="users" menu={<LeftMenu selected="users" />}>
                <div style={{ textAlign: 'center', padding: '100px' }}>
                    <Spin size="large" />
                </div>
            </LayoutLeftMenu>
        );
    }

    if (!member) {
        return (
            <LayoutLeftMenu selected="users" menu={<LeftMenu selected="users" />}>
                <Card>
                    <Empty description={GetText("MemberDetails_NotFound")} />
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <Button
                            type="primary"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(`/${getCurrentLanguage()}/users`)}
                        >
                            {GetText("Common_BackToList")}
                        </Button>
                    </div>
                </Card>
            </LayoutLeftMenu>
        );
    }

    return (
        <LayoutLeftMenu fullpageMain={true} selected="users" menu={<LeftMenu selected="users" />}>
            <Col style={{ marginLeft: "20px", minHeight: "450px" }} span={19}>
                <Row gutter={16}>
                    {/* Main Content - Left Side */}
                    <Col xs={24} lg={16}>
                        <BookyBox alignLeft>
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                {/* Header */}
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        <Button
                                            icon={<ArrowLeftOutlined />}
                                            onClick={() => navigate(`/${getCurrentLanguage()}/users`)}
                                        >
                                            {GetText("Common_Back")}
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <EditMember
                                                user={user!}
                                                membershipProducts={membershipProducts}
                                                memberFieldSettings={memberFieldSettings}
                                                loadMembers={loadMemberDetails}
                                                members={[member]}
                                                groups={[]}
                                                member={member}
                                            />
                                            <AddNote user={user!} member={member} loadMembers={loadMemberDetails} />
                                            <AddPayment member={member} membershipProducts={membershipProducts} loadMembers={loadMemberDetails} />
                                        </Space>
                                    </Col>
                                </Row>

                                {/* Member Name */}
                                <h2 style={{ margin: 0 }}>
                                    <UserOutlined /> {member.firstName} {member.lastName}
                                </h2>

                                {/* Contact Information */}
                                <Card size="small" title={<><MailOutlined /> {GetText("MemberDetails_ContactInfo")}</>}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {member.email && (
                                            <div><strong>{GetText("MemberList_EmailColumn")}:</strong> <a href={`mailto:${member.email}`}>{member.email}</a></div>
                                        )}
                                        {member.email2 && (
                                            <div><strong>{GetText("MemberList_Email2Column")}:</strong> <a href={`mailto:${member.email2}`}>{member.email2}</a></div>
                                        )}
                                        {member.phoneNumber && (
                                            <div><strong>{GetText("MemberList_PhoneNumberColumn")}:</strong> <a href={`tel:${member.phoneNumber}`}>{member.phoneNumberFormatted || member.phoneNumber}</a></div>
                                        )}
                                        {member.phoneNumber2 && (
                                            <div><strong>{GetText("MemberList_PhoneNumber2Column")}:</strong> <a href={`tel:${member.phoneNumber2}`}>{member.phoneNumber2}</a></div>
                                        )}
                                        {user?.companyType === CompanyTypeConfiguration.CRM && member.company && (
                                            <div><strong>{GetText("MemberList_CompanyColumn")}:</strong> {member.company}</div>
                                        )}
                                        {member.webPage && (
                                            <div><strong><GlobalOutlined /> {GetText("MemberList_WebPageColumn")}:</strong> <a href={member.webPage} target="_blank" rel="noopener noreferrer">{member.webPage}</a></div>
                                        )}
                                        <div>
                                            <strong>{GetText("MemberList_EmailNotificationPreferenceColumn")}:</strong>{' '}
                                            {member.emailNotificationPreference === 1
                                                ? GetText("MemberList_NoEmailNotification")
                                                : GetText("MemberList_AcceptsEmailNotification")}
                                        </div>
                                    </Space>
                                </Card>

                                {/* Address Information */}
                                {(member.address || member.postalCode || member.postalArea || member.country) && (
                                    <Card size="small" title={<><HomeOutlined /> {GetText("MemberDetails_AddressInfo")}</>}>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            {member.address && <div>{member.address}</div>}
                                            {member.adressRow2 && <div>{member.adressRow2}</div>}
                                            {member.adressRow3 && <div>{member.adressRow3}</div>}
                                            {(member.postalCode || member.postalArea) && (
                                                <div>{member.postalCode} {member.postalArea}</div>
                                            )}
                                            {member.country && <div>{member.country}</div>}
                                        </Space>
                                    </Card>
                                )}

                                {/* Groups */}
                                {member.groupNames && member.groupNames.length > 0 && (
                                    <Card size="small" title={<><TeamOutlined /> {GetText("MemberList_GroupsColumn")}</>}>
                                        <Space wrap>
                                            {member.groupNames.map(name => <Tag key={name} color="blue">{name}</Tag>)}
                                        </Space>
                                    </Card>
                                )}

                                {/* Membership Information */}
                                {(member.membershipProduct || (member.membershipProductAddOn && member.membershipProductAddOn.length > 0) || member.joinedDate) && (
                                    <Card size="small" title={<><TeamOutlined /> {GetText("MemberDetails_MembershipInfo")}</>}>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            {member.membershipProduct && (
                                                <div>
                                                    <div style={{ marginTop: '4px' }}>
                                                        <Tag color="green">{getMembershipProductName(member.membershipProduct)}</Tag>
                                                    </div>
                                                </div>
                                            )}
                                            {member.membershipProductAddOn && member.membershipProductAddOn.length > 0 && (
                                                <div>
                                                    <div style={{ marginTop: '4px' }}>
                                                        <Space wrap>
                                                            {member.membershipProductAddOn.map(id => {
                                                                const product = membershipProducts.find(p => p.id === id);
                                                                return product ? <Tag key={id} color="cyan">{product.name}</Tag> : null;
                                                            })}
                                                        </Space>
                                                    </div>
                                                </div>
                                            )}
                                            {member.joinedDate && (
                                                <div><strong><CalendarOutlined /> {GetText("MemberList_JoinedDateColumn")}:</strong> {formatDateString(member.joinedDate)}</div>
                                            )}
                                        </Space>
                                    </Card>
                                )}

                                {/* Custom Fields */}
                                {member.customFieldValues && member.customFieldValues.length > 0 && (
                                    <Card size="small" title={GetText("MemberDetails_TabCustomFields")}>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            {member.customFieldValues.map((field, index) => {
                                                const fieldSetting = memberFieldSettings.find(f => f.id === field.fieldId);
                                                return (
                                                    <div key={index}>
                                                        <strong>{fieldSetting?.nameAdmin || fieldSetting?.namePublic || `Field ${index + 1}`}:</strong> {field.fieldValue?.toString() || '-'}
                                                    </div>
                                                );
                                            })}
                                        </Space>
                                    </Card>
                                )}
                            </Space>
                        </BookyBox>
                    </Col>

                    {/* Right Sidebar */}
                    <Col xs={24} lg={8}>
                        <BookyBox alignLeft>
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                {/* Member Info */}
                                <Card size="small" title={GetText("MemberDetails_MemberInfo")}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <div><strong>{GetText("MemberList_UniqueIdColumn")}:</strong> {member.uniqueId || '-'}</div>
                                        <div><strong>{GetText("MemberList_StatusColumn")}:</strong> {getStatusTag(member.status)}</div>
                                        {member.title && (
                                            <div><strong>{GetText("MemberList_TitleColumn")}:</strong> {member.title}</div>
                                        )}
                                        {member.personalNumber && (
                                            <div><strong>{GetText("MemberList_PersonalNumberColumn")}:</strong> {member.personalNumber}</div>
                                        )}
                                    </Space>
                                </Card>

                                {/* Payment Status */}
                                <Card size="small" title={<><DollarOutlined /> {GetText("MemberDetails_PaymentInfo")}</>}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <div>
                                            <strong>{GetText("MemberList_PaymentCurrentYear")}:</strong> {getPaymentStatusTag(member.isPaidCurrentYear)}
                                        </div>
                                        <div>
                                            <strong>{GetText("MemberList_PaymentLastYear")}:</strong> {getPaymentStatusTag(member.isPaidLastYear)}
                                        </div>
                                        {member.paymentLink && (
                                            <div>
                                                <strong>{GetText("MemberList_PaymentLinkColumn")}:</strong>
                                                <div style={{ marginTop: '4px' }}>
                                                    <a href={member.paymentLink} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                                                        {member.paymentLink}
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </Space>
                                </Card>

                                {/* System Info */}
                                <Card size="small" title={GetText("MemberDetails_SystemInfo")}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {member.createdAt && (
                                            <div><strong><CalendarOutlined /> {GetText("MemberList_CreatedColumn")}:</strong> {formatDateString(member.createdAt)}</div>
                                        )}
                                        {member.lastModified && (
                                            <div><strong><CalendarOutlined /> {GetText("MemberList_LastModifiedColumn")}:</strong> {formatDateString(member.lastModified)}</div>
                                        )}
                                    </Space>
                                </Card>

                                {/* Extra Fields */}
                                {(member.extra1 || member.extra2 || member.extra3) && (
                                    <Card size="small" title={GetText("MemberDetails_AdditionalInfo")}>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            {member.extra1 && (
                                                <div><strong>{GetText("MemberList_Extra1Column")}:</strong> {member.extra1}</div>
                                            )}
                                            {member.extra2 && (
                                                <div><strong>{GetText("MemberList_Extra2Column")}:</strong> {member.extra2}</div>
                                            )}
                                            {member.extra3 && (
                                                <div><strong>{GetText("MemberList_Extra3Column")}:</strong> {member.extra3}</div>
                                            )}
                                        </Space>
                                    </Card>
                                )}
                            </Space>
                        </BookyBox>
                    </Col>
                </Row>
            </Col>
        </LayoutLeftMenu>
    );
};

export default MemberDetails;
